
import { Component, Vue } from "vue-property-decorator";
import vPreLoader from "@/components/vPreLoader.vue";
import vTable from "@/components/vTable.vue";
import AddOnLabelPurchaseReportRepository from "@/utils/API/AddOnLabelPurchaseReportRepository";
import {
  AddOnLabelPurchaseReportEntryDto,
  AddOnLabelPurchaseReportFiltersDto,
  AddOnLabelPurchaseReportSummaryDto,
} from "@/models/AddOnLabelPurchaseReport";

type TableFilter = {
  name: string;
  value: string;
};

function formatDateForInput(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function createDefaultFilters(): AddOnLabelPurchaseReportFiltersDto {
  const today = new Date();
  const twelveMonthsAgo = new Date(today.getFullYear() - 1, today.getMonth(), 1);
  return {
    purchasedFrom: formatDateForInput(twelveMonthsAgo),
    purchasedTo: formatDateForInput(today),
    companyName: "",
    customerEmail: "",
    page: 1,
    pageSize: 10,
  };
}

function createDefaultSummary(): AddOnLabelPurchaseReportSummaryDto {
  return {
    purchasingCustomers: 0,
    totalPurchases: 0,
    totalLabelsPurchased: 0,
    mostFrequentQuantity: 0,
    averageLabelsPerPurchase: 0,
    netSales: 0,
    taxAmount: 0,
    grossSales: 0,
  };
}

@Component({ components: { vPreLoader, vTable } })
class AddOnLabelPurchaseReport extends Vue {
  pageSize = 10;
  currentPage = 1;
  count = 0;
  totalCount = 0;
  tableResetKey = 0;
  columnFilters: TableFilter[] = [];
  draftFilters = createDefaultFilters();
  appliedFilters = createDefaultFilters();
  reportEntries: AddOnLabelPurchaseReportEntryDto[] = [];
  summary = createDefaultSummary();
  isLoading = false;

  mounted() {
    this.loadReport();
  }

  get summaryMetrics() {
    return [
      { label: "Purchasing Customers", value: this.summary.purchasingCustomers },
      { label: "Total Purchases", value: this.summary.totalPurchases },
      { label: "Labels Purchased", value: this.summary.totalLabelsPurchased },
      { label: "Most Common Order", value: this.summary.mostFrequentQuantity || "N/A" },
      { label: "Average Order", value: this.summary.averageLabelsPerPurchase.toFixed(1) },
      { label: "Gross Sales", value: this.formatCurrency(this.summary.grossSales) },
    ];
  }

  get tableHeaders() {
    return [
      { name: "Purchased", key: "PurchasedOn", searchable: false, searchString: "", sortable: false },
      { name: "Customer", key: "CustomerName", searchable: false, searchString: "", sortable: false },
      { name: "Company", key: "CompanyName", searchable: true, searchString: "", sortable: false },
      { name: "Email", key: "CustomerEmail", searchable: true, searchString: "", sortable: false },
      { name: "Labels", key: "LabelsPurchased", searchable: false, searchString: "", sortable: false },
      { name: "Unit Price", key: "UnitPrice", searchable: false, searchString: "", sortable: false },
      { name: "Net Sales", key: "NetSales", searchable: false, searchString: "", sortable: false },
      { name: "Tax", key: "TaxAmount", searchable: false, searchString: "", sortable: false },
      { name: "Total", key: "GrossSales", searchable: false, searchString: "", sortable: false },
    ];
  }

  get tableRows() {
    return this.reportEntries.map((entry) => ({
      e1: this.$moment(entry.purchasedOn).format("MMM DD, YYYY h:mm A"),
      e2: entry.customerName,
      e3: entry.companyName,
      e4: entry.customerEmail,
      e5: entry.labelsPurchased,
      e6: this.formatCurrency(entry.unitPrice),
      e7: this.formatCurrency(entry.netSales),
      e8: this.formatCurrency(entry.taxAmount),
      e9: this.formatCurrency(entry.grossSales),
      meta: { id: entry.transactionId },
    }));
  }

  loadReport() {
    this.isLoading = true;
    AddOnLabelPurchaseReportRepository.getReport(this.buildRequestFilters())
      .then((response) => {
        this.reportEntries = Array.isArray(response.data.result) ? response.data.result : [];
        this.summary = response.data.summary || createDefaultSummary();
        this.count = typeof response.data.count === "number" ? response.data.count : this.reportEntries.length;
        this.totalCount = typeof response.data.totalCount === "number" ? response.data.totalCount : this.reportEntries.length;
      })
      .catch(() => {
        this.reportEntries = [];
        this.summary = createDefaultSummary();
        this.count = 0;
        this.totalCount = 0;
        this.$snotify.error("We couldn’t load the add-on labels purchase report right now.");
      })
      .finally(() => { this.isLoading = false; });
  }

  generateReport() {
    if (this.draftFilters.purchasedFrom && this.draftFilters.purchasedTo && this.draftFilters.purchasedFrom > this.draftFilters.purchasedTo) {
      this.$snotify.error("Purchase From cannot be later than Purchase To.");
      return;
    }

    this.appliedFilters = { ...this.draftFilters };
    this.currentPage = 1;
    this.tableResetKey += 1;
    this.loadReport();
  }

  handlePageChange(payload: unknown) {
    if (!Array.isArray(payload) || typeof payload[0] !== "number") return;
    this.currentPage = payload[0];
    this.columnFilters = Array.isArray(payload[2]) ? payload[2] : this.columnFilters;
    this.loadReport();
  }

  handleTableSearch(filters: TableFilter[]) {
    this.columnFilters = Array.isArray(filters) ? filters : [];
    this.currentPage = 1;
    this.loadReport();
  }

  buildRequestFilters(): AddOnLabelPurchaseReportFiltersDto {
    const companyName = this.columnFilters.find((filter) => filter.name === "CompanyName")?.value || "";
    const customerEmail = this.columnFilters.find((filter) => filter.name === "CustomerEmail")?.value || "";
    return {
      ...this.appliedFilters,
      companyName,
      customerEmail,
      page: this.currentPage,
      pageSize: this.pageSize,
    };
  }

  async exportExcel() {
    try {
      const response = await AddOnLabelPurchaseReportRepository.exportExcel(this.buildRequestFilters());
      const blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `add-on-label-purchases-${this.$moment().format("YYYYMMDDHHmmss")}.xlsx`;
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch {
      this.$snotify.error("We couldn’t export the add-on labels report right now.");
    }
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(Number(value || 0));
  }

}

export default AddOnLabelPurchaseReport;
